/**
 * CookiePreferenceCenter — modal listing all consent categories.
 * Opened from the banner (Customize Cookies) OR from the footer link.
 * Portaled + keyboard-accessible (Esc closes, focus trap via autoFocus on Save).
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, Lock, X, Check } from "lucide-react";
import Portal from "@/components/Portal";
import { Switch } from "@/components/ui/switch";
import { useCookieConsent, DEFAULT_CATEGORIES } from "@/contexts/CookieConsentContext";

// Category catalogue — extend this array to add a new category. Keep the `key`
// aligned with a key in `DEFAULT_CATEGORIES` in the context.
const CATEGORY_META = [
  {
    key: "essential",
    label: "Essential Cookies",
    always: true,
    body: "Required for core site functionality: authentication, security tokens, form submissions, and CMS routing. These cannot be disabled.",
  },
  {
    key: "functional",
    label: "Functional Cookies",
    settingsToggle: "cookie_functional_enabled",
    body: "Remember your preferences (e.g. cookie consent itself, language selection, saved filters). Improves usability but not required.",
  },
  {
    key: "analytics",
    label: "Analytics Cookies",
    settingsToggle: "cookie_analytics_enabled",
    body: "Help us understand how visitors use the site — pages viewed, session duration, and traffic sources. Data is aggregated and anonymized (e.g. Google Analytics, Microsoft Clarity).",
  },
  {
    key: "marketing",
    label: "Marketing Cookies",
    settingsToggle: "cookie_marketing_enabled",
    body: "Used by advertising and social platforms to measure campaign effectiveness and deliver relevant content (e.g. Facebook Pixel, LinkedIn Insight).",
  },
];

export default function CookiePreferenceCenter() {
  const { preferenceOpen, closePreferences, preferences, savePreferences, settings } = useCookieConsent();

  const [draft, setDraft] = useState({ ...DEFAULT_CATEGORIES, ...(preferences || {}) });

  // Sync draft when the modal opens
  useEffect(() => {
    if (preferenceOpen) {
      setDraft({ ...DEFAULT_CATEGORIES, ...(preferences || {}) });
    }
  }, [preferenceOpen, preferences]);

  // Esc-to-close
  useEffect(() => {
    if (!preferenceOpen) return;
    const onKey = (e) => { if (e.key === "Escape") closePreferences(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [preferenceOpen, closePreferences]);

  const toggle = (key) => setDraft((d) => ({ ...d, [key]: !d[key] }));

  return (
    <Portal>
      <AnimatePresence>
        {preferenceOpen && (
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="cookie-pref-title"
            data-testid="cookie-preferences"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[85] bg-emerald-ink/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={(e) => { if (e.target === e.currentTarget) closePreferences(); }}
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="bg-white w-full sm:max-w-2xl max-h-[92vh] sm:max-h-[85vh] flex flex-col shadow-2xl border-t-2 sm:border-2 border-gold"
            >
              {/* Header */}
              <div className="p-5 sm:p-6 border-b border-black/10 flex items-start gap-3">
                <Cookie className="w-6 h-6 text-emerald flex-shrink-0 mt-1" aria-hidden />
                <div className="flex-1 min-w-0">
                  <h2 id="cookie-pref-title" className="font-serif text-2xl text-emerald">
                    Cookie Preferences
                  </h2>
                  <p className="text-sm text-foreground/60 mt-1">
                    Choose which cookies you allow this website to use. You can change your choices any time from the footer.
                  </p>
                </div>
                <button
                  onClick={closePreferences}
                  aria-label="Close cookie preferences"
                  data-testid="cookie-pref-close"
                  className="p-1 text-foreground/40 hover:text-foreground"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Categories */}
              <div className="p-5 sm:p-6 overflow-y-auto flex-1 space-y-4">
                {CATEGORY_META.map((cat) => {
                  const adminDisabled = cat.settingsToggle && settings[cat.settingsToggle] === false;
                  const on = cat.always ? true : (adminDisabled ? false : !!draft[cat.key]);
                  return (
                    <div
                      key={cat.key}
                      data-testid={`cookie-cat-${cat.key}`}
                      className={`border border-black/10 p-4 sm:p-5 ${adminDisabled ? "opacity-50 bg-muted/30" : ""}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-emerald">{cat.label}</h3>
                            {cat.always && (
                              <span className="inline-flex items-center gap-1 text-[0.65rem] uppercase tracking-widest text-neon bg-neon/10 border border-neon/30 px-1.5 py-0.5">
                                <Lock className="w-3 h-3" /> Always On
                              </span>
                            )}
                            {adminDisabled && !cat.always && (
                              <span className="text-[0.65rem] uppercase tracking-widest text-bronze bg-bronze/10 border border-bronze/30 px-1.5 py-0.5">
                                Disabled by admin
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-foreground/65 mt-1.5 leading-relaxed">{cat.body}</p>
                        </div>
                        <Switch
                          checked={on}
                          disabled={cat.always || adminDisabled}
                          onCheckedChange={() => toggle(cat.key)}
                          data-testid={`cookie-cat-${cat.key}-switch`}
                          aria-label={`Toggle ${cat.label}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="p-4 sm:p-5 bg-muted/20 border-t border-black/10 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 justify-end">
                <button
                  onClick={closePreferences}
                  data-testid="cookie-pref-cancel"
                  className="px-4 py-2 border border-black/15 text-xs uppercase tracking-widest hover:border-emerald hover:text-emerald transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => savePreferences(draft)}
                  data-testid="cookie-pref-save"
                  autoFocus
                  className="px-5 py-2.5 bg-emerald text-bone hover:bg-emerald-deep text-xs uppercase tracking-widest inline-flex items-center gap-2 transition-colors"
                >
                  <Check className="w-4 h-4" /> Save Preferences
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Portal>
  );
}